import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Menu, X, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ContactModal from './ContactModal';

const Navbar = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <nav className="fixed w-full top-0 z-50 glass border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2"
            >
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_document-pro-2/artifacts/fsrl1cy2_Logo%20da%20Minha%20Composi%C3%A7%C3%A3o%203.png" 
                  alt="Minha Composição" 
                  className="h-10 w-auto"
                  onError={(e) => {
                    // Fallback caso a imagem não carregue
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="items-center space-x-2 hidden">
                  <Music className="w-8 h-8 text-blue-600" />
                  <span className="text-xl font-bold text-gray-900">ABRI</span>
                </div>
              </Link>
            </motion.div>

            {/* Desktop Menu */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex items-center space-x-6"
            >
              <Link 
                to="/#como-funciona" 
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                {t('nav.howItWorks')}
              </Link>
              <Link 
                to="/#comparativo"
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    document.getElementById('comparativo')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                Comparativo
              </Link>
              <Link 
                to="/#pricing"
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                {t('nav.plans')}
              </Link>
              <Link 
                to="/tendencias"
                className="text-gray-700 hover:text-orange-600 transition-colors font-medium flex items-center gap-1"
              >
                🔥 Top 50
              </Link>
              <Link 
                to="/gallery"
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium flex items-center gap-1"
              >
                🎵 Galeria
              </Link>
              <Link 
                to="/gallery/lyrics"
                className="text-gray-700 hover:text-purple-600 transition-colors font-medium flex items-center gap-1"
              >
                📄 Letras
              </Link>
              <button
                onClick={() => setShowContactModal(true)}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Fale Conosco
              </button>
              <LanguageSelector />
              <Link
                to="/login"
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-gradient-primary text-white px-6 py-2.5 rounded-xl hover:shadow-lg transition-all ripple font-semibold"
              >
                {t('nav.register')}
              </Link>
            </motion.div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenu(!mobileMenu)}
              className="md:hidden text-gray-700 hover:text-blue-600"
            >
              {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenu && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed inset-y-0 right-0 w-64 bg-white shadow-2xl z-50 md:hidden"
          >
            <div className="flex flex-col p-6 space-y-6 mt-16">
              <Link 
                to="/#como-funciona" 
                onClick={(e) => {
                  setMobileMenu(false);
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    setTimeout(() => {
                      document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
                className="text-gray-700 hover:text-blue-600 font-medium"
              >
                {t('nav.howItWorks')}
              </Link>
              <Link 
                to="/#comparativo"
                onClick={(e) => {
                  setMobileMenu(false);
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    setTimeout(() => {
                      document.getElementById('comparativo')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
                className="text-gray-700 hover:text-blue-600 font-medium"
              >
                Comparativo
              </Link>
              <Link 
                to="/#pricing"
                onClick={(e) => {
                  setMobileMenu(false);
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    setTimeout(() => {
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
                className="text-gray-700 hover:text-blue-600 font-medium"
              >
                {t('nav.plans')}
              </Link>
              <Link 
                to="/tendencias" 
                onClick={() => setMobileMenu(false)} 
                className="text-gray-700 hover:text-orange-600 font-medium"
              >
                🔥 Top 50 Brasil
              </Link>
              <Link 
                to="/gallery" 
                onClick={() => setMobileMenu(false)} 
                className="text-gray-700 hover:text-blue-600 font-medium"
              >
                🎵 Galeria de Áudios
              </Link>
              <Link 
                to="/gallery/lyrics" 
                onClick={() => setMobileMenu(false)} 
                className="text-gray-700 hover:text-purple-600 font-medium"
              >
                📄 Galeria de Letras
              </Link>
              <button
                onClick={() => {
                  setMobileMenu(false);
                  setShowContactModal(true);
                }}
                className="text-gray-700 hover:text-blue-600 font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Fale Conosco
              </button>
              <Link to="/login" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-blue-600 font-medium">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileMenu(false)}
                className="bg-gradient-primary text-white px-6 py-3 rounded-xl text-center font-semibold"
              >
                {t('nav.register')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
    </>
  );
};

export default Navbar;
